/**
 * Request Deduplicator — Prevents duplicate in-flight requests.
 *
 * WHY THIS EXISTS:
 * When multiple components call the same async function (e.g., fetchProfile)
 * simultaneously, we want to:
 * 1. Execute the request ONCE
 * 2. Share the result with all callers
 * 3. Clean up after completion
 *
 * USAGE:
 * ```typescript
 * // Instead of:
 * const profile = await fetchProfile(token);
 *
 * // Use:
 * const profile = await deduplicatedFetch(
 *   `profile-${userId}`,
 *   () => fetchProfile(token)
 * );
 * ```
 *
 * WHEN TO USE:
 * ✅ Auth/profile fetching (called from multiple layouts/components)
 * ✅ Feature flags initialization
 * ✅ Global settings/preferences
 * ❌ TanStack Query hooks (already deduplicated internally)
 */

// Map of in-flight requests by key
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pendingRequests = new Map<string, Promise<any>>();

/**
 * Execute a fetch function with automatic deduplication.
 *
 * If a request with the same key is already in-flight, returns the existing
 * promise instead of starting a new request.
 *
 * @param key - Unique identifier for the request (e.g., "profile-123")
 * @param fetcher - Async function that performs the actual request
 * @returns Promise that resolves to the fetcher's result
 *
 * @example
 * const user = await deduplicatedFetch(
 *   `user-${userId}`,
 *   () => api.getUser(userId)
 * );
 */
export async function deduplicatedFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  // If request is already in-flight, return existing promise
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key)!;
  }

  // Start new request
  const promise = fetcher().finally(() => {
    // Clean up after request completes (success or failure)
    pendingRequests.delete(key);
  });

  pendingRequests.set(key, promise);
  return promise;
}

/**
 * Clear all pending requests (useful for testing or logout).
 */
export function clearPendingRequests(): void {
  pendingRequests.clear();
}

/**
 * Check if a request is currently in-flight.
 *
 * @param key - Request key to check
 * @returns true if request is pending, false otherwise
 */
export function isPending(key: string): boolean {
  return pendingRequests.has(key);
}

/**
 * Get the number of currently pending requests (debugging).
 */
export function getPendingCount(): number {
  return pendingRequests.size;
}
